import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Container,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Visibility as VisibilityIcon,
  ArrowBack as ArrowBackIcon,
  Image as ImageIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  ArrowForward as ArrowForwardIcon,
  Home as HomeIcon,
  Lock as LockIcon,
  Star as StarIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import {
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import config from '../config';
import { compressImageIfNeeded, getCompressionInfo, getCompressionOptions } from '../utils/imageCompression';

// Build a display label combining the ad type with any add-ons, e.g. "Standard - Top Spot"
const formatAdTypeWithAddons = (adType, addons) => {
  const baseType = adType || 'N/A';
  if (!addons) return baseType;
  const addonNames = [];
  if (addons.topSpot) addonNames.push('Top Spot');
  if (addons.gif) addonNames.push('GIF Animation');
  return addonNames.length > 0 ? `${baseType} - ${addonNames.join(', ')}` : baseType;
};

const ProofCreate = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [proofImages, setProofImages] = useState({});
  const [proofEmbeds, setProofEmbeds] = useState({});
  const [contentType, setContentType] = useState({}); // 'image' or 'embed' per group
  const [uploading, setUploading] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);

  // Fetch order details
  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const token = await getToken();
        
        // Try different endpoints to fetch the order
        let response;
        let orderData;
        
        // Try ad-orders first
        try {
          response = await axios.get(`${config.apiUrl}/api/ad-orders/${orderId}`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          if (response.data && response.data.success) {
            orderData = response.data.data;
          }
        } catch (adErr) {
          
          // If ad-orders fails, try stationery-orders
          try {
            response = await axios.get(`${config.apiUrl}/api/stationery-orders/${orderId}`, {
              headers: {
                Authorization: `Bearer ${token}`
              }
            });
            if (response.data && response.data.success) {
              orderData = response.data.data;
            }
          } catch (stationeryErr) {
            throw new Error('Order not found in any endpoint');
          }
        }

        if (orderData) {
          setOrder(orderData);
          
          // Initialize proof images state
          const initialImages = {};
          
          // Check if this is a stationery order
          if (orderData.orderType === 'stationery') {
            // For stationery orders, just initialize a single image slot
            initialImages['stationery_proof'] = null;
            setProofImages(initialImages);
          } else {
            // For ad orders, initialize for each ad group
            const audienceGroups = {};
            
            // Ensure all audiences have proper ad group assignments
            const processedAudiences = orderData.audiences?.map((audience, index) => {
              // If no adGroup is set, assign based on index + 1 or existing logic
              let groupNumber = audience.adGroup;
              
              if (!groupNumber || groupNumber === null || groupNumber === undefined) {
                // Default assignment: first audience gets group 1, others get sequential numbers
                // or same group if they have the same adType
                if (index === 0) {
                  groupNumber = 1;
                } else {
                  // Check if there's an existing audience with the same adType
                  const sameTypeAudience = orderData.audiences.slice(0, index).find(
                    prevAudience => prevAudience.adType === audience.adType
                  );
                  
                  if (sameTypeAudience && sameTypeAudience.adGroup) {
                    groupNumber = sameTypeAudience.adGroup;
                  } else {
                    // Find the highest existing group number and add 1
                    const existingGroups = orderData.audiences.slice(0, index)
                      .map(aud => parseInt(aud.adGroup) || 0)
                      .filter(group => group > 0);
                    groupNumber = existingGroups.length > 0 ? Math.max(...existingGroups) + 1 : index + 1;
                  }
                }
              }
              
              groupNumber = parseInt(groupNumber) || 1;
              audienceGroups[groupNumber] = true;
              
              return {
                ...audience,
                adGroup: groupNumber
              };
            }) || [];
            
            // Update the order with processed audiences
            setOrder(prev => ({
              ...prev,
              audiences: processedAudiences
            }));
            
            // Initialize proof images for each group
            Object.keys(audienceGroups).forEach(groupNumber => {
              initialImages[`group_${groupNumber}`] = null;
            });
            
            setProofImages(initialImages);
          }
        } else {
          setError('Order not found');
        }
      } catch (err) {
        console.error('Error fetching order:', err);
        setError('Failed to fetch order details');
      } finally {
        setLoading(false);
      }
    };

    if (orderId) {
      fetchOrder();
    }
  }, [orderId, getToken]);

  const handleImageUpload = async (audienceId, event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type first
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setError(`Invalid file type for "${file.name}". Please use JPEG, PNG, GIF, or WebP images only.`);
        event.target.value = ''; // Reset the input
        return;
      }

      // Clear any previous errors
      setError(null);

      try {
        // Show compression status
        setProofImages(prev => ({
          ...prev,
          [audienceId]: {
            file: null,
            preview: null,
            name: file.name,
            compressing: true
          }
        }));

        // Process the image (compress if needed)
        const compressionOptions = getCompressionOptions('proof');
        const processedFile = await compressImageIfNeeded(file, compressionOptions);
        
        // Create a preview URL for the processed image
        const imageUrl = URL.createObjectURL(processedFile);
        
        // Get compression info
        const compressionInfo = getCompressionInfo(file, processedFile);
        
        setProofImages(prev => ({
          ...prev,
          [audienceId]: {
            file: processedFile,
            preview: imageUrl,
            name: processedFile.name || file.name,
            originalSize: compressionInfo.originalSize,
            compressedSize: compressionInfo.compressedSize,
            compressionRatio: compressionInfo.compressionRatio,
            wasCompressed: compressionInfo.wasCompressed,
            compressing: false
          }
        }));

      } catch (error) {
        console.error('Error processing image:', error);
        setError(`Failed to process image "${file.name}": ${error.message}`);
        setProofImages(prev => ({
          ...prev,
          [audienceId]: null
        }));
        event.target.value = ''; // Reset the input
      }
    }
  };

  const handleRemoveImage = (audienceId) => {
    if (proofImages[audienceId]?.preview) {
      URL.revokeObjectURL(proofImages[audienceId].preview);
    }
    setProofImages(prev => ({
      ...prev,
      [audienceId]: null
    }));
  };

  const handleEmbedChange = (audienceId, embedCode) => {
    setProofEmbeds(prev => ({
      ...prev,
      [audienceId]: {
        code: embedCode,
        preview: embedCode // Use embed code as preview
      }
    }));
  };

  const handleRemoveEmbed = (audienceId) => {
    setProofEmbeds(prev => ({
      ...prev,
      [audienceId]: null
    }));
  };

  const handleContentTypeChange = (audienceId, type) => {
    setContentType(prev => ({
      ...prev,
      [audienceId]: type
    }));
    
    // Clear existing content when switching types
    if (type === 'image') {
      handleRemoveEmbed(audienceId);
    } else {
      handleRemoveImage(audienceId);
    }
  };

  // Test function to check server connectivity
  const testServerConnection = async () => {
    try {
      await axios.get(`${config.apiUrl}/api/health`);
      return true;
    } catch (error) {
      console.error('Server connection test failed:', error);
      return false;
    }
  };

  const handleCreateProof = async () => {
    setUploading(true);
    try {
      // Test server connection first
      const serverReachable = await testServerConnection();
      if (!serverReachable) {
        throw new Error('Server connection test failed. Please check your network connection.');
      }

      const token = await getToken();
      
      // Handle stationery orders differently
      if (isStationeryOrder) {
        // For stationery orders, we only need a single image
        const stationeryImage = proofImages['stationery_proof'];
        if (!stationeryImage?.file) {
          throw new Error('Please upload a proof image');
        }
        
        try {
          const base64 = await convertFileToBase64(stationeryImage.file);
          
          const response = await axios.post(
            `${config.apiUrl}/api/proof/create/${orderId}`,
            {
              proofImage: base64
            },
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );
          
          if (response.data && response.data.success) {
            const proofUrl = response.data.proofUrl;
            
            // Show success dialog with proof URL
            const confirmed = window.confirm(
              `Proof website created successfully!\n\n` +
              `You can now share this link with your client:\n${proofUrl}\n\n` +
              `Would you like to open the proof website now?`
            );
            
            if (confirmed) {
              window.open(proofUrl, '_blank');
            }
            
            navigate('/app/stationery-orders');
          } else {
            throw new Error(response.data?.message || 'Failed to create proof website');
          }
        } catch (convertError) {
          console.error('Error converting image:', convertError);
          throw new Error('Failed to process proof image');
        }
      } else {
        // Process both images and embeds for ad orders
        const processedContent = {};
        
        for (const groupNumber of Object.keys(audienceGroups)) {
          const groupKey = `group_${groupNumber}`;
          const type = contentType[groupKey] || 'image';
          
          if (type === 'image' && proofImages[groupKey]?.file) {
            try {
              const base64 = await convertFileToBase64(proofImages[groupKey].file);
              processedContent[groupKey] = {
                type: 'image',
                data: base64,
                filename: proofImages[groupKey].name
              };
            } catch (convertError) {
              console.error(`Error converting image for ${groupKey}:`, convertError);
              throw new Error(`Failed to process image for ${groupKey}`);
            }
          } else if (type === 'embed' && proofEmbeds[groupKey]?.code) {
            processedContent[groupKey] = {
              type: 'embed',
              data: proofEmbeds[groupKey].code
            };
          }
        }
        
        // Calculate approximate payload size for debugging
        const payloadSize = JSON.stringify({ proofContent: processedContent }).length;
        
        const response = await axios.post(
          `${config.apiUrl}/api/proof/create/${orderId}`,
          {
            proofContent: processedContent,
            proofStyle: 'standard'
          },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (response.data && response.data.success) {
          const proofUrl = response.data.proofUrl;
          
          // Show success dialog with proof URL
          const confirmed = window.confirm(
            `Proof website created successfully!\n\n` +
            `You can now share this link with your client:\n${proofUrl}\n\n` +
            `Would you like to open the proof website now?`
          );
          
          if (confirmed) {
            window.open(proofUrl, '_blank');
          }
          
          navigate('/app/order-workflow');
        } else {
          throw new Error(response.data?.message || 'Failed to create proof website');
        }
      }
      
    } catch (err) {
      console.error('Error creating proof:', err);
      console.error('Error response data:', err.response?.data);
      console.error('Error status:', err.response?.status);
      console.error('Error headers:', err.response?.headers);
      
      let errorMessage = 'Failed to create proof website';
      
      if (err.response?.status === 404) {
        errorMessage = 'Proof creation endpoint not found. The server may not be running or the endpoint may not be registered correctly.';
      } else if (err.response?.status === 413) {
        errorMessage = 'The uploaded images are too large. Please try using smaller images (under 10MB each) or fewer images per request.';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setUploading(false);
    }
  };



  // Helper function to convert file to base64
  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Remove the data:image/jpeg;base64, prefix to get just the base64 string
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  };

  const handlePreviewProof = () => {
    setPreviewDialogOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewDialogOpen(false);
  };

  const handleBackToWorkflow = () => {
    // Navigate back based on order type
    if (order?.orderType === 'stationery') {
      navigate('/app/stationery-orders');
    } else {
      navigate('/app/order-workflow');
    }
  };

  if (loading) {
    return (
      <Container maxWidth={false}>
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth={false}>
        <Box mt={4}>
          <Alert severity="error">{error}</Alert>
          <Button onClick={handleBackToWorkflow} sx={{ mt: 2 }}>
            Back to Order Workflow
          </Button>
        </Box>
      </Container>
    );
  }

  if (!order) {
    return (
      <Container maxWidth={false}>
        <Box mt={4}>
          <Alert severity="warning">Order not found</Alert>
          <Button onClick={handleBackToWorkflow} sx={{ mt: 2 }}>
            Back to Order Workflow
          </Button>
        </Box>
      </Container>
    );
  }

  const businessName = order?.organizationName || order?.businessName || 'Unknown Business';
  const isStationeryOrder = order?.orderType === 'stationery';

  // Get add-ons from order audiences
  const getOrderAddons = () => {
    if (!order?.audiences || order.audiences.length === 0) {
      return [];
    }
    
    const addons = [];
    const addonsByGroup = {};
    
    order.audiences.forEach((audience) => {
      const group = audience.adGroup || 1;
      if (!addonsByGroup[group]) {
        addonsByGroup[group] = [];
      }
      
      if (audience.addons) {
        if (audience.addons.topSpot) {
          addonsByGroup[group].push('Top Spot');
        }
        if (audience.addons.gif) {
          addonsByGroup[group].push('GIF Animation');
        }
      }
    });
    
    // Convert to display format
    Object.entries(addonsByGroup).forEach(([group, groupAddons]) => {
      if (groupAddons.length > 0) {
        const uniqueAddons = [...new Set(groupAddons)];
        addons.push({
          group: parseInt(group),
          addons: uniqueAddons
        });
      }
    });
    
    return addons.sort((a, b) => a.group - b.group);
  };

  const orderAddons = getOrderAddons();

  // Group audiences by ad group - improved version
  const groupAudiencesByAdGroup = (audiences) => {
    if (!audiences) return {};
    
    const groups = {};
    audiences.forEach((audience, index) => {
      // Ensure we have a valid group number
      let groupNumber = audience.adGroup;
      
      if (!groupNumber || groupNumber === null || groupNumber === undefined) {
        console.warn(`Audience ${index} missing adGroup, defaulting to group 1:`, audience);
        groupNumber = 1;
      }
      
      groupNumber = parseInt(groupNumber);
      
      if (isNaN(groupNumber) || groupNumber < 1) {
        console.warn(`Invalid adGroup for audience ${index}, defaulting to group 1:`, audience);
        groupNumber = 1;
      }
      
      if (!groups[groupNumber]) {
        groups[groupNumber] = [];
      }
      
      groups[groupNumber].push({
        ...audience,
        adGroup: groupNumber // Ensure the group number is set correctly
      });
    });
    
    return groups;
  };

  // For stationery orders, create a single group
  const audienceGroups = isStationeryOrder ? { 1: [] } : groupAudiencesByAdGroup(order?.audiences);
  
  // Check if all ad groups have content (images or embeds)
  const hasAllContent = isStationeryOrder 
    ? proofImages['stationery_proof'] && 
      !proofImages['stationery_proof'].compressing &&
      proofImages['stationery_proof'].file
    : Object.keys(audienceGroups).every(groupNumber => {
        const groupKey = `group_${groupNumber}`;
        const type = contentType[groupKey] || 'image';
        
        if (type === 'image') {
          return proofImages[groupKey] && 
                 !proofImages[groupKey].compressing &&
                 proofImages[groupKey].file;
        } else {
          return proofEmbeds[groupKey] && proofEmbeds[groupKey].code.trim();
        }
      });

  // Check if any images are currently compressing
  const isCompressing = Object.values(proofImages).some(image => image?.compressing);

  return (
    <Container maxWidth={false}>
      <Box sx={{ mt: 2, mb: 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={handleBackToWorkflow} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            {businessName} {isStationeryOrder ? 'Stationery' : 'Advertising'} Proof
          </Typography>
        </Box>

        {/* Order Info */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Order Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">
                <strong>Order ID:</strong> {order.id}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">
                <strong>Status:</strong> {order.status}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">
                <strong>Business Name:</strong> {businessName}
              </Typography>
            </Grid>
            {isStationeryOrder ? (
              <>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2">
                    <strong>Organisation:</strong> {order.organizationName || order.businessName || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2">
                    <strong>Product Type:</strong> {order.productType || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2">
                    <strong>Quantity:</strong> {order.quantity || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2">
                    <strong>Size:</strong> {order.size || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2">
                    <strong>Sides:</strong> {order.sides === '1' ? 'Single Sided' : order.sides === '2' ? 'Double Sided' : order.sides || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2">
                    <strong>Stock:</strong> {order.stock || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2">
                    <strong>Finish:</strong> {order.finish || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2">
                    <strong>Price:</strong> ${parseFloat(order.price || 0).toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2">
                    <strong>Freight Price:</strong> ${parseFloat(order.freightPrice || 0).toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2">
                    <strong>Additional Artwork Hours:</strong> {order.additionalArtworkHours || '0'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2">
                    <strong>Discount:</strong> {order.discountType === '%' 
                      ? `${order.discountAmount || 0}%` 
                      : `$${parseFloat(order.discountAmount || 0).toFixed(2)}`}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2">
                    <strong>Total Price:</strong> ${parseFloat(order.totalPrice || 0).toFixed(2)}
                  </Typography>
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2">
                    <strong>Total Audiences:</strong> {order.audiences?.length || 0}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2">
                    <strong>Ad Groups:</strong> {Object.keys(audienceGroups).length}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2">
                    <strong>Group Numbers:</strong> {Object.keys(audienceGroups).sort((a, b) => parseInt(a) - parseInt(b)).join(', ')}
                  </Typography>
                </Grid>
              </>
            )}
            {!isStationeryOrder && orderAddons.length > 0 && (
              <Grid item xs={12}>
                <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, borderLeft: '3px solid', borderLeftColor: 'warning.main' }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary', mb: 0.5 }}>
                    Add-ons Included:
                  </Typography>
                  {orderAddons.map(({ group, addons }) => (
                    <Typography key={group} variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      <strong>Group {group}:</strong> {addons.join(', ')}
                    </Typography>
                  ))}
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', fontStyle: 'italic' }}>
                    Please ensure these add-ons are reflected in your proof materials.
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Ad Groups Summary or Stationery Summary */}
        {!isStationeryOrder && (
          <Paper sx={{ p: 3, mb: 3, backgroundColor: '#f8f9fa' }}>
            <Typography variant="h6" gutterBottom>
              Ad Groups Summary
            </Typography>
            <Grid container spacing={2}>
              {Object.entries(audienceGroups).map(([groupNumber, audiences]) => (
                <Grid item xs={12} sm={6} md={4} key={groupNumber}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent sx={{ pb: 1 }}>
                      <Typography variant="subtitle1" color="primary" gutterBottom>
                        Group {groupNumber}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        School: {[...new Set(audiences.map(a => a.schoolName || a.organizationName || businessName || 'Unknown'))].join(', ')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Channel: {[...new Set(audiences.map(a => a.channel))].join(', ')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Ad Type: {formatAdTypeWithAddons(audiences[0]?.adType, audiences[0]?.addons)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Audiences: {audiences.length}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        )}

        {/* Content Upload Section */}
        <Typography variant="h5" gutterBottom>
          {isStationeryOrder ? 'Upload Proof Image' : 'Provide Creative Assets by Ad Group'}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {isStationeryOrder 
            ? 'Upload an image of the stationery design proof for client approval.'
            : 'Choose to upload images or provide embed codes for each ad group. All audiences in the same ad group will use the same creative asset.'
          }
        </Typography>

        {!isStationeryOrder && Object.keys(audienceGroups).length === 0 && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            No ad groups found. Please check that your order has audiences with valid ad group assignments.
          </Alert>
        )}

        {/* Stationery Order Upload */}
        {isStationeryOrder ? (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Proof Image
                </Typography>
                
                {proofImages['stationery_proof'] ? (
                  proofImages['stationery_proof'].compressing ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        p: 3,
                        border: '2px dashed',
                        borderColor: 'primary.main',
                        borderRadius: 1
                      }}
                    >
                      <CircularProgress sx={{ mb: 2 }} />
                      <Typography variant="body2" color="text.secondary">
                        Processing image...
                      </Typography>
                    </Box>
                  ) : (
                    <Box>
                      <Box sx={{ position: 'relative', mb: 2 }}>
                        <img
                          src={proofImages['stationery_proof'].preview}
                          alt="Proof"
                          style={{ width: '100%', maxHeight: '500px', objectFit: 'contain', borderRadius: '8px' }}
                        />
                        <IconButton
                          onClick={() => handleRemoveImage('stationery_proof')}
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            bgcolor: 'error.main',
                            color: 'white',
                            '&:hover': { bgcolor: 'error.dark' }
                          }}
                        >
                          <CloseIcon />
                        </IconButton>
                      </Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {proofImages['stationery_proof'].name}
                      </Typography>
                      {proofImages['stationery_proof'].wasCompressed && (
                        <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1 }}>
                          ✓ Image compressed: {proofImages['stationery_proof'].originalSize} → {proofImages['stationery_proof'].compressedSize} ({proofImages['stationery_proof'].compressionRatio}% reduction)
                        </Typography>
                      )}
                    </Box>
                  )
                ) : (
                  <Box>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="stationery-proof-upload"
                      type="file"
                      onChange={(e) => handleImageUpload('stationery_proof', e)}
                    />
                    <label htmlFor="stationery-proof-upload">
                      <Box
                        sx={{
                          border: '2px dashed',
                          borderColor: 'grey.300',
                          borderRadius: 1,
                          p: 4,
                          textAlign: 'center',
                          cursor: 'pointer',
                          '&:hover': {
                            borderColor: 'primary.main',
                            bgcolor: 'action.hover'
                          }
                        }}
                      >
                        <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                        <Typography variant="body1" gutterBottom>
                          Click to upload proof image
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Supports: JPEG, PNG, GIF, WebP
                        </Typography>
                      </Box>
                    </label>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Paper>
        ) : (
          /* Ad Orders - Multiple Ad Groups */
          Object.entries(audienceGroups)
          .sort(([a], [b]) => parseInt(a) - parseInt(b)) // Sort by group number
          .map(([groupNumber, audiences]) => {
            const groupKey = `group_${groupNumber}`;
            const currentType = contentType[groupKey] || 'image';
            
            return (
              <Box key={groupNumber} sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                  Ad Group {groupNumber}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Ad Type: {formatAdTypeWithAddons(audiences[0]?.adType || audiences[0]?.ad_type, audiences[0]?.addons)} • 
                  {audiences.length} audience{audiences.length > 1 ? 's' : ''} • 
                  Schools: {[...new Set(audiences.map(a => a.schoolName || a.organizationName || businessName || 'Unknown'))].join(', ')}
                </Typography>
                
                <Grid container spacing={3}>
                  {/* Content type selection and upload */}
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Group {groupNumber} Creative Asset
                        </Typography>
                        
                        {/* Content Type Toggle */}
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Choose content type:
                          </Typography>
                          <ToggleButtonGroup
                            value={currentType}
                            exclusive
                            onChange={(e, newType) => newType && handleContentTypeChange(groupKey, newType)}
                            size="small"
                            fullWidth
                          >
                            <ToggleButton value="image">
                              <ImageIcon sx={{ mr: 1 }} />
                              Upload Image
                            </ToggleButton>
                            <ToggleButton value="embed">
                              <CloseIcon sx={{ mr: 1 }} />
                              Embed Code
                            </ToggleButton>
                          </ToggleButtonGroup>
                        </Box>
                        
                        <Divider sx={{ my: 2 }} />

                        {/* Image Upload Content */}
                        {currentType === 'image' && (
                          <>
                            {proofImages[groupKey] ? (
                              proofImages[groupKey].compressing ? (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    p: 3,
                                    textAlign: 'center'
                                  }}
                                >
                                  <CircularProgress size={48} sx={{ mb: 2 }} />
                                  <Typography variant="body2" color="text.secondary">
                                    Compressing "{proofImages[groupKey].name}"...
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                                    Optimizing image for web delivery
                                  </Typography>
                                </Box>
                              ) : (
                                <Box>
                                  <img
                                    src={proofImages[groupKey].preview}
                                    alt={`Proof for ad group ${groupNumber}`}
                                    style={{
                                      width: '100%',
                                      maxHeight: '200px',
                                      objectFit: 'contain',
                                      border: '1px solid #ddd',
                                      borderRadius: '4px'
                                    }}
                                  />
                                  <Typography variant="body2" sx={{ mt: 1 }}>
                                    {proofImages[groupKey].name}
                                  </Typography>
                                  {/* Compression Info */}
                                  {proofImages[groupKey].originalSize && 
                                   proofImages[groupKey].compressedSize && (
                                    <Box sx={{ mt: 1, p: 1, bgcolor: '#f0f7ff', borderRadius: 1 }}>
                                      <Typography variant="caption" color="text.secondary" display="block">
                                        Original: {(proofImages[groupKey].originalSize / 1024 / 1024).toFixed(2)} MB
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" display="block">
                                        Optimized: {(proofImages[groupKey].compressedSize / 1024 / 1024).toFixed(2)} MB
                                      </Typography>
                                      {proofImages[groupKey].compressionRatio > 0 && (
                                        <Typography variant="caption" color="success.main" display="block" sx={{ fontWeight: 600 }}>
                                          {proofImages[groupKey].compressionRatio}% size reduction
                                        </Typography>
                                      )}
                                    </Box>
                                  )}
                                </Box>
                              )
                            ) : (
                              <Box
                                sx={{
                                  border: '2px dashed #ccc',
                                  borderRadius: '4px',
                                  p: 3,
                                  textAlign: 'center',
                                  backgroundColor: '#fafafa'
                                }}
                              >
                                <ImageIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
                                <Typography variant="body2" color="text.secondary">
                                  No image uploaded
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                  Images will be automatically optimized for web delivery
                                </Typography>
                              </Box>
                            )}

                            {/* Image Upload Actions */}
                            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                              <Button
                                component="label"
                                variant="outlined"
                                startIcon={<CloudUploadIcon />}
                                fullWidth
                                disabled={proofImages[groupKey]?.compressing}
                              >
                                {proofImages[groupKey]?.compressing ? 'Processing...' : 
                                 proofImages[groupKey] ? 'Replace Image' : 'Upload Image'}
                                <input
                                  type="file"
                                  hidden
                                  accept="image/*"
                                  onChange={(e) => handleImageUpload(groupKey, e)}
                                  disabled={proofImages[groupKey]?.compressing}
                                />
                              </Button>
                              {proofImages[groupKey] && !proofImages[groupKey].compressing && (
                                <Button
                                  variant="outlined"
                                  color="error"
                                  onClick={() => handleRemoveImage(groupKey)}
                                  sx={{ minWidth: 'auto', px: 2 }}
                                >
                                  <CloseIcon />
                                </Button>
                              )}
                            </Box>
                          </>
                        )}

                        {/* Embed Code Content */}
                        {currentType === 'embed' && (
                          <>
                            <TextField
                              fullWidth
                              multiline
                              rows={6}
                              label="Embed Code"
                              placeholder="Paste your HTML embed code here (iframe, script tags, etc.)"
                              value={proofEmbeds[groupKey]?.code || ''}
                              onChange={(e) => handleEmbedChange(groupKey, e.target.value)}
                              variant="outlined"
                              sx={{ mb: 2 }}
                              helperText="Supports HTML, iframe, script tags, and other embed codes"
                            />
                            
                            {proofEmbeds[groupKey]?.code && (
                              <Box>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                  Preview:
                                </Typography>
                                <Box
                                  sx={{
                                    border: '1px solid #ddd',
                                    borderRadius: 1,
                                    p: 2,
                                    bgcolor: '#f9f9f9',
                                    maxHeight: '300px',
                                    overflow: 'auto',
                                    '& iframe': {
                                      maxWidth: '100%',
                                      border: 'none'
                                    }
                                  }}
                                >
                                  <div 
                                    dangerouslySetInnerHTML={{ __html: proofEmbeds[groupKey].code }}
                                    style={{ 
                                      minHeight: '50px',
                                      position: 'relative'
                                    }}
                                  />
                                </Box>
                                
                                {/* Show raw code for debugging */}
                                <Box sx={{ mt: 1 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Embed Code ({proofEmbeds[groupKey].code.length} characters):
                                  </Typography>
                                  <Box
                                    sx={{
                                      bgcolor: '#f5f5f5',
                                      p: 1,
                                      borderRadius: 1,
                                      fontFamily: 'monospace',
                                      fontSize: '0.75rem',
                                      maxHeight: '100px',
                                      overflow: 'auto',
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-all'
                                    }}
                                  >
                                    {proofEmbeds[groupKey].code.substring(0, 200)}
                                    {proofEmbeds[groupKey].code.length > 200 && '...'}
                                  </Box>
                                </Box>
                                
                                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                                  <Button
                                    variant="outlined"
                                    color="error"
                                    onClick={() => handleRemoveEmbed(groupKey)}
                                    startIcon={<CloseIcon />}
                                    fullWidth
                                  >
                                    Clear Embed Code
                                  </Button>
                                </Box>
                              </Box>
                            )}
                          </>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* List of audiences in this group */}
                  <Grid item xs={12} md={6}>
                    <Card sx={{ backgroundColor: '#f9f9f9' }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Audiences in Group {groupNumber}
                        </Typography>
                        {audiences.map((audience, index) => (
                          <Box key={audience.id || index} sx={{ mb: 2, p: 1, backgroundColor: 'white', borderRadius: 1 }}>
                            <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                              {audience.schoolName || audience.organizationName || businessName || `Audience ${index + 1}`}
                            </Typography>
                            <Typography variant="body2" gutterBottom>
                              <strong>Channel:</strong> {audience.channel}
                            </Typography>
                            <Typography variant="body2" gutterBottom>
                              <strong>Ad Type:</strong> {formatAdTypeWithAddons(audience.adType || audience.ad_type, audience.addons)}
                            </Typography>
                            <Typography variant="body2" gutterBottom>
                              <strong>Duration:</strong> {audience.startDate && audience.endDate ? 
                                `${new Date(audience.startDate).toLocaleDateString('en-AU')} - ${new Date(audience.endDate).toLocaleDateString('en-AU')}` : 
                                'Not specified'}
                            </Typography>
                            <Typography variant="body2" gutterBottom>
                              <strong>Cost:</strong> ${parseFloat(audience.price || 0).toFixed(2)}
                            </Typography>
                            {audience.discount && parseFloat(audience.discount) > 0 && (
                              <Typography variant="body2" gutterBottom>
                                <strong>Discount:</strong> ${parseFloat(audience.discount).toFixed(2)}
                              </Typography>
                            )}
                            {audience.eLinkType && audience.eLinkType !== 'None' && (
                              <>
                                <Typography variant="body2" gutterBottom>
                                  <strong>Link Type:</strong> {audience.eLinkType}
                                </Typography>
                                {audience.eLinkContent && (
                                  <Typography variant="body2" gutterBottom>
                                    <strong>Link Content:</strong> {audience.eLinkContent}
                                  </Typography>
                                )}
                              </>
                            )}
                          </Box>
                        ))}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            );
          })
        )}

        {/* Action Buttons */}
        <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button
            variant="contained"
            startIcon={<StarIcon />}
            onClick={handleCreateProof}
            disabled={!hasAllContent || uploading || isCompressing}
          >
            {uploading ? 'Creating...' : 'Create Proof'}
          </Button>
        </Box>

        {isCompressing && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Processing images... Please wait for compression to complete.
          </Alert>
        )}

        {!hasAllContent && !isCompressing && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {isStationeryOrder 
              ? 'Please upload a proof image before creating the proof.'
              : 'Please provide content (images or embed codes) for all ad groups before creating the proof.'
            }
          </Alert>
        )}
      </Box>

    </Container>
  );
};

// ProofPreviewContent component for the clean proof layout
const ProofPreviewContent = ({ order, proofImages, businessName }) => {
  if (!order.audiences || order.audiences.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>No audiences available for preview</Typography>
      </Box>
    );
  }

  // Group audiences by ad group
  const groupAudiencesByAdGroup = (audiences) => {
    if (!audiences) return {};
    
    const groups = {};
    audiences.forEach((audience, index) => {
      let groupNumber = audience.adGroup || 1;
      groupNumber = parseInt(groupNumber);
      
      if (isNaN(groupNumber) || groupNumber < 1) {
        groupNumber = 1;
      }
      
      if (!groups[groupNumber]) {
        groups[groupNumber] = [];
      }
      
      groups[groupNumber].push({
        ...audience,
        adGroup: groupNumber
      });
    });
    
    return groups;
  };

  const audienceGroups = groupAudiencesByAdGroup(order.audiences);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Browser Header */}
      <Box sx={{ 
        backgroundColor: '#f5f5f5', 
        borderBottom: '1px solid #ddd',
        p: 1
      }}>
        {/* Browser Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', gap: 0.5, mr: 2 }}>
            <Box sx={{ 
              width: 12, 
              height: 12, 
              borderRadius: '50%', 
              backgroundColor: '#ff5f57' 
            }} />
            <Box sx={{ 
              width: 12, 
              height: 12, 
              borderRadius: '50%', 
              backgroundColor: '#ffbd2e' 
            }} />
            <Box sx={{ 
              width: 12, 
              height: 12, 
              borderRadius: '50%', 
              backgroundColor: '#28ca42' 
            }} />
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1, mr: 2 }}>
            <IconButton size="small" disabled>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" disabled>
              <ArrowForwardIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" disabled>
              <RefreshIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" disabled>
              <HomeIcon fontSize="small" />
            </IconButton>
          </Box>
          
          <Box sx={{ 
            flexGrow: 1, 
            backgroundColor: 'white', 
            borderRadius: 1,
            px: 2,
            py: 0.5,
            display: 'flex',
            alignItems: 'center',
            border: '1px solid #ddd'
          }}>
            <LockIcon sx={{ fontSize: 16, color: '#4caf50', mr: 1 }} />
            <Typography variant="body2" sx={{ color: '#666' }}>
              austnews.com.au/advertising/proof-preview
            </Typography>
          </Box>
        </Box>

        {/* Single Tab */}
        <Box sx={{ 
          backgroundColor: 'white',
          border: '1px solid #ccc',
          borderBottom: 'none',
          borderRadius: '8px 8px 0 0',
          px: 2,
          py: 0.5,
          display: 'inline-block',
          mr: 0.5
        }}>
          <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
            {businessName} - Advertising Proof
          </Typography>
        </Box>
      </Box>

      {/* Proof Content - Clean Grid Layout */}
      <Box sx={{ flexGrow: 1, backgroundColor: 'white', overflow: 'auto', p: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ textAlign: 'center', mb: 3 }}>
          {businessName} - Advertising Proof
        </Typography>
        
        <Grid container spacing={3}>
          {Object.entries(audienceGroups)
            .sort(([a], [b]) => parseInt(a) - parseInt(b))
            .map(([groupNumber, audiences]) => {
              const schools = [...new Set(audiences.map(a => a.schoolName || a.organizationName || businessName || 'Unknown'))];
              const channels = [...new Set(audiences.map(a => a.channel))];
              const adType = formatAdTypeWithAddons(audiences[0]?.adType || audiences[0]?.ad_type, audiences[0]?.addons);
              const startDate = audiences[0]?.startDate;
              const endDate = audiences[0]?.endDate;
              const totalCost = audiences.reduce((sum, aud) => sum + parseFloat(aud.price || 0), 0);
              
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={groupNumber}>
                  <Card sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    border: '2px solid #e0e0e0',
                    '&:hover': {
                      boxShadow: 3
                    }
                  }}>
                    <CardContent sx={{ flexGrow: 1 }}>
                      {/* Ad Group Number */}
                      <Typography variant="h6" sx={{ 
                        color: 'primary.main', 
                        fontWeight: 'bold', 
                        mb: 2,
                        textAlign: 'center',
                        backgroundColor: '#f5f5f5',
                        py: 1,
                        borderRadius: 1
                      }}>
                        Ad Group {groupNumber}
                      </Typography>
                      
                      {/* List of Schools */}
                      <Typography variant="body2" gutterBottom>
                        <strong>Schools:</strong>
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1.5, fontStyle: schools.length > 1 ? 'normal' : 'italic' }}>
                        {schools.join(', ')}
                      </Typography>
                      
                      {/* Channel */}
                      <Typography variant="body2" gutterBottom>
                        <strong>Channel:</strong> {channels.join(', ')}
                      </Typography>
                      
                      {/* Ad Type */}
                      <Typography variant="body2" gutterBottom>
                        <strong>Ad Type:</strong> {adType}
                      </Typography>
                      
                      {/* Duration */}
                      <Typography variant="body2" gutterBottom>
                        <strong>Duration:</strong> {startDate && endDate ? 
                          `${new Date(startDate).toLocaleDateString('en-AU')} - ${new Date(endDate).toLocaleDateString('en-AU')}` : 
                          'Not specified'}
                      </Typography>
                      
                      {/* Cost */}
                      <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
                        <strong>Cost:</strong> ${totalCost.toFixed(2)}
                      </Typography>
                      
                      {/* Image/Asset */}
                      <Typography variant="body2" gutterBottom>
                        <strong>Image/Asset:</strong>
                      </Typography>
                      {proofImages[`group_${groupNumber}`] ? (
                        <Box sx={{ textAlign: 'center', mt: 1 }}>
                          <img
                            src={proofImages[`group_${groupNumber}`].preview}
                            alt={`Ad Group ${groupNumber} Asset`}
                            style={{
                              width: '100%',
                              maxHeight: '200px',
                              objectFit: 'contain',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              backgroundColor: '#fafafa'
                            }}
                          />
                        </Box>
                      ) : (
                        <Box sx={{ 
                          textAlign: 'center', 
                          mt: 1,
                          p: 2,
                          border: '2px dashed #ccc',
                          borderRadius: 1,
                          backgroundColor: '#fafafa'
                        }}>
                          <ImageIcon sx={{ fontSize: 40, color: '#ccc', mb: 1 }} />
                          <Typography variant="body2" color="text.secondary">
                            No Image Uploaded
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
        </Grid>
        
        {Object.keys(audienceGroups).length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              No ad groups found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please ensure your order has audiences with valid ad group assignments.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ProofCreate; 